"use client";

/* eslint-disable react/jsx-props-no-spreading */

import { ReactElement, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import CookiePolicyButton from "@/app/shared-components/cookie-policy-button/cookie-policy-button";
import CountryChangeModal from "@/app/shared-components/country-change-modal/country-change-modal";
import QuestionFooterSection from "@/app/shared-components/question-footer-section";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import ReviewPageButton from "@/components/atoms/review-page-button/review-page-button";
import ReviewTitle from "@/components/atoms/review-tilte/review-title";
import AddPromoCode from "@/components/molecules/review-section/add-promo-code";
import BeforeContinue from "@/components/molecules/review-section/before-continue";
import PolicyRenewal from "@/components/molecules/review-section/policy-renewal";
import PostalSetting from "@/components/molecules/review-section/postal-settings";
import QuoteSummary from "@/components/molecules/review-section/quote-summary";
import { ANNUAL_MULTI_TRIP } from "@/constants";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { useLoading } from "@/providers/loading-provider";
import { RootState } from "@/store/store";
import { CombinedReviewContentType } from "@/utils/schema/review-page-schema.utils";

import CancellationCoverApiErrorModal from "../molecules/api-error-modal/cancellation-cover-api-error-modal";
import FloatingChatButton from "../molecules/floating-chat-button/floating-chat-button";
import PassportIdSection from "../molecules/review-section/passport-id-block/passport-id-block";
import TravelEligibilitySection from "../molecules/review-section/travel-eligibility-block/travel-eligibility-block";
import PaymentMessages from "../organisms/review-section/review-payment-message";
import withBrand from "../with-brand";
import withQuoteData from "../with-quote-data";

interface ReviewPageProps {
  content: CombinedReviewContentType;
  helpButton: ReactElement;
  logoWithProps: ReactElement;
  reviewCover: ReactElement;
  brand?: string;
}

function ReviewPage(props: Readonly<ReviewPageProps>) {
  const { content, helpButton, logoWithProps, reviewCover, brand } = props;
  const { attributes, referenceId } = useSelector(
    (state: RootState) => state.quote
  );
  const { current, wizard } = useWizard();
  const { productSpecialFeatures } = wizard;
  const { showLoading, hideLoading } = useLoading();
  const [passportId, setPassportId] = useState<string | undefined>();
  const isDesktop = useMediaQuery("(min-width: 904px)");
  const chatButtonLabelMobile =
    content?.leftAreaBeforeContinue[0]?.chatButtonLabelMobile;
  const chatButtonLabelDesktop =
    content?.leftAreaBeforeContinue[0]?.chatButtonLabelDesktop;
  useEffect(() => {
    showLoading();
    if (referenceId) {
      hideLoading();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceId]);

  return (
    <>
      <main
        className=" bg-surface-neutral-option-2 !min-h-[100vh]"
        data-testid="review-page-testid"
      >
        <CancellationCoverApiErrorModal brand={brand} />
        {productSpecialFeatures?.showCountryFooter && <CountryChangeModal />}
        <div className="max-w-[1200px] mx-auto py-spacing-l md:py-spacing-xl lg:py-spacing-xxl">
          <PaymentMessages />
          <div className=" grid grid-cols-12 lg:gap-spacing-xxl relative lg:mx-auto md:mx-[48px] mx-[24px] ">
            <div className="lg:col-span-7 col-span-12 mt-spacing-l lg:mt-0 order-last lg:order-first">
              <div>
                <div className=" flex flex-col bg-surface-neutral-option-1 rounded-radius-xxl">
                  <div className="bg-surface-neutral-option-1 text-purple-900 rounded-t-radius-xxl min-h-[64px] md:min-h-[80px] relative hidden lg:block">
                    {isDesktop && helpButton}
                    {logoWithProps}
                  </div>
                  <div
                    data-testid="PolicyRenewal"
                    className=" rounded-b-radius-xxl px-spacing-m pt-spacing-m pb-spacing-l md:px-spacing-xxxl lg:px-spacing-6xl md:py-spacing-xxl"
                  >
                    <div className=" font-header-font md:text-[39px] text-[23px] lg:leading-[44.85px] md:leading-[34.1px] leading-[29.9px] text-text-title text-center md:text-left hidden lg:block mb-spacing-l">
                      <ReviewTitle {...(content?.titleArea["0"] ?? null)} />
                    </div>
                    <div>
                      <div>
                        <BeforeContinue
                          {...(content?.leftAreaBeforeContinue["0"] ?? null)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {current.uiElementControls?.showQuoteSummarySection && (
                  <div className="mt-spacing-l md:mt-spacing-xl lg:mt-spacing-l px-spacing-m pt-spacing-m pb-spacing-l lg:px-spacing-6xl md:px-spacing-xxxl md:py-spacing-xxl py-spacing-xxl bg-surface-neutral-option-1 rounded-radius-xxl mb-spacing-l md:mb-0 ">
                    <QuoteSummary
                      {...(content?.quoteSummaryArea?.["0"] ?? null)}
                    />
                  </div>
                )}

                {attributes?.tripDetails?.policyType === ANNUAL_MULTI_TRIP &&
                  current.uiElementControls?.hideAutoRenewalConsent !==
                    true && (
                    <div className=" mt-spacing-l md:mt-spacing-xl lg:mt-spacing-l px-spacing-m pt-spacing-m pb-spacing-l lg:px-spacing-6xl md:px-spacing-xxxl md:py-spacing-xxl py-spacing-xxl bg-surface-neutral-option-1 rounded-radius-xxl mb-spacing-l md:mb-0 ">
                      <PolicyRenewal
                        {...(content?.leftAreaPolicyRenewal["0"] ?? null)}
                      />
                    </div>
                  )}

                {current.uiElementControls?.showPassportIdSection && (
                  <div className=" mt-spacing-l md:mt-spacing-xl lg:mt-spacing-l px-spacing-m pt-spacing-m pb-spacing-l lg:px-spacing-6xl md:px-spacing-xxxl md:py-spacing-xxl py-spacing-xxl bg-surface-neutral-option-1 rounded-radius-xxl mb-spacing-l md:mb-0 ">
                    <PassportIdSection
                      {...(content?.passportIdArea["0"] ?? null)}
                      setPassportId={setPassportId}
                    />
                  </div>
                )}

                <div className=" mt-spacing-l md:mt-spacing-xl lg:mt-spacing-l px-spacing-m pt-spacing-m pb-spacing-l lg:px-spacing-6xl md:px-spacing-xxxl md:py-spacing-xxl py-spacing-xxl bg-surface-neutral-option-1 rounded-radius-xxl mb-spacing-l md:mb-0 ">
                  <PostalSetting
                    {...(content?.leftAreaPostalSettings["0"] ?? null)}
                  />
                </div>

                <div className=" mt-spacing-l md:mt-spacing-xl lg:mt-spacing-l px-spacing-m pt-spacing-m pb-spacing-l lg:px-spacing-6xl md:px-spacing-xxxl md:py-spacing-xxl py-spacing-xxl bg-surface-neutral-option-1 rounded-radius-xxl mb-[80px] md:mb-0 ">
                  <TravelEligibilitySection
                    {...(content?.travelEligibilityArea["0"] ?? null)}
                  />
                </div>

                <div className="w-[414px] mx-auto hidden md:block lg:hidden md:mt-[39px] lg:mt-0">
                  <ReviewPageButton
                    passportIdValue={passportId}
                    textBtn={content?.buttonArea["0"].textBtn ?? null}
                  />
                </div>
              </div>
            </div>

            <div className=" lg:col-span-5 col-span-12 ">
              <div>
                <div className=" rounded-radius-xxl bg-dark-surface-accent-1-primary text-surface-neutral-option-2 ">
                  {reviewCover}
                </div>

                <div className="mt-spacing-l md:mt-spacing-xl lg:mt-spacing-l px-spacing-m py-spacing-m md:pb-spacing-xl md:pt-spacing-xxl md:px-spacing-xxxl lg:py-spacing-xxl lg:px-spacing-xxl bg-surface-neutral-option-1 rounded-radius-xxl">
                  <AddPromoCode
                    {...(content?.leftPromoCodeArea["0"] ?? null)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="w-[414px] mx-auto block md:hidden lg:block lg:mt-spacing-xxl">
            <ReviewPageButton
              passportIdValue={passportId}
              textBtn={content?.buttonArea["0"].textBtn ?? null}
            />
          </div>
          <div className=" relative z-10 md:mt-[-30px] mb-[80px] mt-[-80px] lg:mt-[-10px] md:mb-[60px] lg:mb-0">
            <QuestionFooterSection />
          </div>
          {productSpecialFeatures?.useNewHelpOverlay && (
            <div className=" relative z-0 md:mt-[-30px] mb-[80px] mt-[-80px] lg:mt-[-10px] md:mb-[60px] lg:mb-0">
              <CookiePolicyButton />
            </div>
          )}
        </div>
      </main>

      <FloatingChatButton
        chatButtonLabelMobile={chatButtonLabelMobile}
        chatButtonLabelDesktop={chatButtonLabelDesktop}
        show={productSpecialFeatures?.showNewChatButton}
      />
    </>
  );
}

export default withQuoteData(withBrand(ReviewPage));
